import { useState, useCallback } from "react";
import { Clock, Plus } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import { useShifts, useCreateShift } from "@/hooks/admin/useOperations";
import type { Shift, ShiftStatus, CreateShiftInput } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<ShiftStatus, string> = {
  scheduled: "Planifié",
  active: "En cours",
  completed: "Terminé",
  absent: "Absent",
};

const STATUS_COLORS: Record<ShiftStatus, string> = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-stone-100 text-stone-600 border-stone-200",
  absent: "bg-red-100 text-red-800 border-red-200",
};

const REGIONS = [
  "Dakar",
  "Kaolack",
  "Saint-Louis",
  "Fatick",
  "Thies-Mbour",
  "Diourbel-Touba",
];

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "region",
    label: "Région",
    type: "select",
    options: REGIONS.map((r) => ({ value: r, label: r })),
  },
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: [
      { value: "scheduled", label: "Planifié" },
      { value: "active", label: "En cours" },
      { value: "completed", label: "Terminé" },
      { value: "absent", label: "Absent" },
    ],
  },
  { key: "date_from", label: "Du", type: "date" },
  { key: "date_to", label: "Au", type: "date" },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDateTime(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Create shift modal
// ---------------------------------------------------------------------------

interface CreateShiftModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateShiftInput) => Promise<void>;
}

function CreateShiftModal({ open, onClose, isSaving, onSave }: CreateShiftModalProps) {
  const [agentId, setAgentId] = useState("");
  const [region, setRegion] = useState(REGIONS[0]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setAgentId("");
    setRegion(REGIONS[0]);
    setStartTime("");
    setEndTime("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!agentId.trim()) {
      setError("L'ID de l'agent est requis.");
      return;
    }
    if (!startTime) {
      setError("L'heure de début est requise.");
      return;
    }
    if (!endTime) {
      setError("L'heure de fin est requise.");
      return;
    }
    setError(null);
    try {
      await onSave({
        agent_id: agentId.trim(),
        region,
        start_time: startTime,
        end_time: endTime,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [agentId, region, startTime, endTime, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Planifier un quart de travail"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Création…" : "Planifier"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            ID de l'agent <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="UUID de l'agent"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">Région</label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            {REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Début <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Fin <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Shifts() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showCreate, setShowCreate] = useState(false);

  const params = {
    page,
    limit: 20,
    region: (filters.region as string) ?? undefined,
    status: (filters.status as ShiftStatus) ?? undefined,
    date_from: (filters.date_from as string) ?? undefined,
    date_to: (filters.date_to as string) ?? undefined,
  };

  const { data, isLoading } = useShifts(params);
  const createMutation = useCreateShift();

  const shifts = data?.shifts ?? [];
  const pagination = data?.pagination;

  const today = new Date().toDateString();
  const scheduledToday = shifts.filter(
    (s) => s.status === "scheduled" && new Date(s.start_time).toDateString() === today,
  ).length;
  const activeNow = shifts.filter((s) => s.status === "active").length;
  const absentCount = shifts.filter((s) => s.status === "absent").length;
  const totalRelevant = shifts.filter(
    (s) => new Date(s.start_time).toDateString() === today,
  ).length;
  const attendanceRate =
    totalRelevant > 0
      ? Math.round(((totalRelevant - absentCount) / totalRelevant) * 100)
      : 0;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateShiftInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const columns: readonly ColumnDef<Shift>[] = [
    {
      id: "agent_name",
      header: "Agent",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.agent_name}</span>
      ),
    },
    {
      id: "region",
      header: "Région",
      accessorKey: "region",
    },
    {
      id: "start_time",
      header: "Début",
      cell: (row) => (
        <span className="text-stone-600">{formatDateTime(row.start_time)}</span>
      ),
    },
    {
      id: "end_time",
      header: "Fin",
      cell: (row) => (
        <span className="text-stone-500">{formatDateTime(row.end_time)}</span>
      ),
    },
    {
      id: "check_in_at",
      header: "Check-in",
      cell: (row) => (
        <span className="text-stone-500">{formatDateTime(row.check_in_at)}</span>
      ),
    },
    {
      id: "check_out_at",
      header: "Check-out",
      cell: (row) => (
        <span className="text-stone-500">{formatDateTime(row.check_out_at)}</span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      id: "notes",
      header: "Notes",
      cell: (row) => (
        <span className="max-w-[120px] truncate text-xs text-stone-400">
          {row.notes ?? "—"}
        </span>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Planning équipes
              </h1>
              <p className="text-sm text-stone-500">Quarts de travail des agents</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Planifier un quart
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Planifiés aujourd'hui" value={scheduledToday} />
          <KPICard label="Actifs maintenant" value={activeNow} />
          <KPICard label="Absents" value={absentCount} />
          <KPICard label="Taux présence" value={attendanceRate} format="percent" />
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar filters={FILTER_CONFIGS} values={filters} onChange={handleFilterChange} />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={shifts}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucun quart de travail trouvé."
        />
      </div>

      <CreateShiftModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}
